/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Contacts component for Settings Profile tab
 *
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.3.0
 */

import { Fragment } from 'react';

import { Checkbox } from '../../../../../components/common';
import { EmailInput } from '../../../../../components/common/EmailInput/EmailInput';
import { PhoneInput } from '../../../../../components/common/PhoneInput/PhoneInput';
import ICommonTranslation from '../../../../../modules/internationalization/types/common';
import { IPageSettingsTranslation } from '../../../../../modules/internationalization/types/pages/pageSettings';
import { PageSettingsHeaderStyled } from '../../styled/PageSettingsStyled';
import { ESettingsProfileValidationCodes } from '../settingsProfileConstants';
import { ISettingsProfileParamsContacts, TSettingsProfileSetParamContactsActionPayload } from '../settingsProfileTypes';
import {
    SettingsProfileContactsParamContainerStyled,
    SettingsProfileLabelStyled,
} from '../styled/SettingsProfileStyled';

export interface ISettingsProfileContactsProps {
    commonTranslation: ICommonTranslation;
    params: ISettingsProfileParamsContacts;
    setParam: (payload: TSettingsProfileSetParamContactsActionPayload) => void;
    settingsTranslation: IPageSettingsTranslation;
}

export const SettingsProfileContacts = (props: ISettingsProfileContactsProps) => {
    const { contacts: translationContacts } = props.settingsTranslation.profile;

    const handlePhoneChange = (phoneValue: string) => {
        const payload: TSettingsProfileSetParamContactsActionPayload = phoneValue ?
            { phone: phoneValue } : { phone: phoneValue, phoneIsEnabled: false }
        props.setParam(payload)
    };

    const handleEmailChange = (emailValue: string) => {
        const payload: TSettingsProfileSetParamContactsActionPayload = emailValue ?
            { email: emailValue } : { email: emailValue, emailIsEnabled: false }
        props.setParam(payload)
    };

    const handlePhoneValidate = (isValid: boolean) => {
        if (isValid === !props.params.phoneErrorCode) return;

        props.setParam({ phoneErrorCode: isValid ? '' : ESettingsProfileValidationCodes.PHONE_INCORRECT })
    };

    const handleEmailValidate = (isValid: boolean) => {
        if (isValid === !props.params.emailErrorCode) return;

        props.setParam({ emailErrorCode: isValid ? '' : ESettingsProfileValidationCodes.EMAIL_INCORRECT })
    };

    return (
        <Fragment>
            <PageSettingsHeaderStyled>
                {translationContacts.header}
            </PageSettingsHeaderStyled>

            <SettingsProfileContactsParamContainerStyled>
                <SettingsProfileLabelStyled>
                    {translationContacts.phoneLabel}
                </SettingsProfileLabelStyled>
                <PhoneInput
                    isAllowEmpty
                    dataName='contactsPhoneInput'
                    isValid={!props.params.phoneErrorCode}
                    phoneInputValue={props.params.phone}
                    phoneMask={props.commonTranslation.phoneInput.mask}
                    phonePlaceholder={translationContacts.phonePlaceholder}
                    translation={props.commonTranslation}
                    onPhoneInputChange={handlePhoneChange}
                    onPhoneInputValidate={handlePhoneValidate}

                />
                <Checkbox
                    checked={props.params.phoneIsEnabled}
                    dataName='contactsPhoneCheckbox'
                    disabled={!props.params.phone}
                    label={translationContacts.phoneNotify}
                    onChange={phoneIsEnabled => props.setParam({ phoneIsEnabled })}
                />
            </SettingsProfileContactsParamContainerStyled>

            <SettingsProfileContactsParamContainerStyled>
                <SettingsProfileLabelStyled>
                    {translationContacts.emailLabel}
                </SettingsProfileLabelStyled>
                <EmailInput
                    isAllowEmpty
                    dataName='contactsEmailInput'
                    emailInputValue={props.params.email}
                    emailPlaceholder={translationContacts.emailPlaceholder}
                    isValid={!props.params.emailErrorCode}
                    translation={props.commonTranslation}
                    onEmailInputChange={handleEmailChange}
                    onEmailInputValidate={handleEmailValidate}
                />
                <Checkbox
                    checked={props.params.emailIsEnabled}
                    dataName='contactsEmailCheckbox'
                    disabled={!props.params.email}
                    label={translationContacts.emailNotify}
                    onChange={emailIsEnabled => props.setParam({ emailIsEnabled })}
                />
            </SettingsProfileContactsParamContainerStyled>
        </Fragment>
    )
};
